import React, { useState, useEffect } from "react";
import {
  FaDna,
  FaShieldAlt,
  FaExclamationTriangle,
  FaExternalLinkAlt,
  FaCheckCircle,
  FaFilter,
  FaSearch,
  FaVial,
  FaPills,
  FaHeartbeat,
} from "react-icons/fa";
import { EpletClientService } from "../../../utils/epletService";

const EpletAnalysisComponent = ({
  recipient,
  donor,
  recipientName,
  donorName,
  recipientClinicals,
  donorClinicals,
}) => {
  const [analysis, setAnalysis] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [locusFilter, setLocusFilter] = useState("ALL");
  const [evidenceFilter, setEvidenceFilter] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");

  const token = localStorage.getItem("token");

  useEffect(() => {
    const runAnalysis = async () => {
      setIsLoading(true);
      try {
        const fallbackPair = {
          recipient_clinical: recipientClinicals,
          donor_clinical: donorClinicals,
        };

        const result = await EpletClientService.fetchAnalysis(
          recipient,
          donor,
          token,
          fallbackPair
        );

        setAnalysis(result);
      } catch (err) {
        console.error("Eplet analysis failed:", err);
      } finally {
        setIsLoading(false);
      }
    };

    if (recipient && donor) {
      runAnalysis();
    }
  }, [recipient, donor, recipientClinicals, donorClinicals, token]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-white rounded-lg shadow-sm">
        <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mb-3"></div>
        <p className="text-sm font-medium text-gray-600">
          Calculating molecular eplet mismatches from EpRegistry...
        </p>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
        <FaExclamationTriangle className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
        <h4 className="text-md font-semibold text-yellow-800">
          HLA Typing Required for Eplet Analysis
        </h4>
        <p className="text-xs text-yellow-700 mt-1">
          Please verify that both recipient and donor have complete HLA typing records in Clinical Management.
        </p>
      </div>
    );
  }

  // Filtered eplets
  const filteredEplets = analysis.mismatchedEplets.filter((item) => {
    if (locusFilter !== "ALL") {
      if (locusFilter === "CLASS_I" && item.locus !== "ABC") return false;
      if (locusFilter === "DRB" && item.locus !== "DRB") return false;
      if (locusFilter === "DQ" && item.locus !== "DQ") return false;
      if (locusFilter === "DP" && item.locus !== "DP") return false;
    }
    if (evidenceFilter !== "ALL" && item.evidence !== evidenceFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        item.id.toLowerCase().includes(q) ||
        item.residues.toLowerCase().includes(q) ||
        (item.sourceAllele && item.sourceAllele.toLowerCase().includes(q)) ||
        (item.description && item.description.toLowerCase().includes(q))
      );
    }
    return true;
  });

  const getTierBadge = () => {
    switch (analysis.riskTier) {
      case "LOW":
        return {
          bg: "bg-green-100 text-green-800 border-green-300",
          icon: <FaCheckCircle className="mr-1 text-green-600" />,
        };
      case "INTERMEDIATE":
        return {
          bg: "bg-yellow-100 text-yellow-800 border-yellow-300",
          icon: <FaExclamationTriangle className="mr-1 text-yellow-600" />,
        };
      case "HIGH":
      case "CONTRAINDICATED":
        return {
          bg: "bg-red-100 text-red-800 border-red-300",
          icon: <FaShieldAlt className="mr-1 text-red-600" />,
        };
      default:
        return {
          bg: "bg-gray-100 text-gray-800 border-gray-300",
          icon: null,
        };
    }
  };

  const tierBadge = getTierBadge();

  return (
    <div className="space-y-6">
      {/* 1. Header Overview Banner */}
      <div className="p-5 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-xl shadow-sm">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="p-2 bg-indigo-600 text-white rounded-lg">
                <FaDna className="w-5 h-5" />
              </span>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Molecular HLA Eplet Analysis
                </h2>
                <p className="text-xs text-gray-500">
                  Powered by the International HLA Epitope Registry (
                  <a
                    href="https://www.epregistry.com.br"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:underline font-semibold"
                  >
                    www.epregistry.com.br
                  </a>
                  )
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div
              className={`flex items-center px-3.5 py-1.5 rounded-full text-xs font-bold border ${tierBadge.bg}`}
            >
              {tierBadge.icon}
              {analysis.riskTierLabel}
            </div>
            <div className="flex items-center px-3.5 py-1.5 bg-indigo-100 text-indigo-800 rounded-full text-xs font-bold">
              Eplet Score: {analysis.epletScore}%
            </div>
          </div>
        </div>

        {/* 2. Key Metrics Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-4">
          <div className="p-3 bg-white rounded-lg border border-gray-200 text-center shadow-xs">
            <span className="text-[11px] font-semibold text-gray-500 block uppercase">
              Total Mismatches
            </span>
            <span className="text-2xl font-black text-gray-900">
              {analysis.totalMismatches}
            </span>
            <span className="text-[10px] text-gray-500 block">
              {analysis.confirmedMismatches} Ab-Verified
            </span>
          </div>

          <div className="p-3 bg-white rounded-lg border border-gray-200 text-center shadow-xs">
            <span className="text-[11px] font-semibold text-gray-500 block uppercase">
              Class I (A, B, C)
            </span>
            <span className={`text-2xl font-black ${analysis.classI > 16 ? "text-red-600" : analysis.classI >= 10 ? "text-yellow-600" : "text-green-600"}`}>
              {analysis.classI}
            </span>
            <span className="text-[10px] text-gray-500 block">Threshold: &lt;10</span>
          </div>

          <div className="p-3 bg-white rounded-lg border border-gray-200 text-center shadow-xs">
            <span className="text-[11px] font-semibold text-gray-500 block uppercase">
              HLA-DRB1 Load
            </span>
            <span className={`text-2xl font-black ${analysis.drb1 > 10 ? "text-red-600" : analysis.drb1 >= 7 ? "text-yellow-600" : "text-green-600"}`}>
              {analysis.drb1}
            </span>
            <span className="text-[10px] text-gray-500 block">Threshold: &lt;7</span>
          </div>

          <div className="p-3 bg-white rounded-lg border border-gray-200 text-center shadow-xs">
            <span className="text-[11px] font-semibold text-gray-500 block uppercase">
              HLA-DQB1 Load
            </span>
            <span className={`text-2xl font-black ${analysis.dqb1 > 14 ? "text-red-600" : analysis.dqb1 >= 9 ? "text-yellow-600" : "text-green-600"}`}>
              {analysis.dqb1}
            </span>
            <span className="text-[10px] text-gray-500 block">Threshold: &lt;9</span>
          </div>

          <div className="p-3 bg-white rounded-lg border border-gray-200 text-center shadow-xs">
            <span className="text-[11px] font-semibold text-gray-500 block uppercase">
              HLA-DPB1 Load
            </span>
            <span className="text-2xl font-black text-gray-900">
              {analysis.dpb1}
            </span>
            <span className="text-[10px] text-gray-500 block">Eplets</span>
          </div>

          <div className="p-3 bg-white rounded-lg border border-gray-200 text-center shadow-xs">
            <span className="text-[11px] font-semibold text-gray-500 block uppercase">
              dnDSA 5-Yr Risk
            </span>
            <span className={`text-sm font-extrabold ${analysis.riskTier === "HIGH" || analysis.riskTier === "CONTRAINDICATED" ? "text-red-600" : analysis.riskTier === "INTERMEDIATE" ? "text-yellow-600" : "text-green-600"}`}>
              {analysis.dnDsaPrediction}
            </span>
            <span className="text-[10px] text-gray-500 block">Landmark Cohort</span>
          </div>
        </div>
      </div>

      {/* 3. Actionable Clinical Management Pathways for Physicians */}
      <div className="p-4 bg-white border border-gray-200 rounded-xl shadow-xs">
        <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2 mb-3">
          <FaPills className="text-indigo-600" />
          Physician Clinical Guidance & Personalized Immunosuppression
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
          <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
            <span className="font-bold text-gray-700 block mb-1 flex items-center gap-1">
              <FaShieldAlt className="text-indigo-500" /> Induction Strategy:
            </span>
            <p className="text-gray-600 leading-relaxed">
              {analysis.recommendations.induction}
            </p>
          </div>

          <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
            <span className="font-bold text-gray-700 block mb-1 flex items-center gap-1">
              <FaPills className="text-purple-500" /> CNI Target & Sparing:
            </span>
            <p className="text-gray-600 leading-relaxed">
              {analysis.recommendations.cni}
            </p>
          </div>

          <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
            <span className="font-bold text-gray-700 block mb-1 flex items-center gap-1">
              <FaHeartbeat className="text-red-500" /> Post-Op Surveillance:
            </span>
            <p className="text-gray-600 leading-relaxed">
              {analysis.recommendations.surveillance}
            </p>
          </div>
        </div>
      </div>

      {/* 4. Filter & Search Controls */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs">
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <FaFilter className="text-gray-500" />
          <span className="font-semibold text-gray-700">Locus:</span>
          <select
            value={locusFilter}
            onChange={(e) => setLocusFilter(e.target.value)}
            className="p-1.5 bg-white border border-gray-300 rounded font-medium text-gray-700"
          >
            <option value="ALL">All Loci ({analysis.totalMismatches})</option>
            <option value="CLASS_I">Class I (ABC) ({analysis.classI})</option>
            <option value="DRB">HLA-DRB1 ({analysis.drb1})</option>
            <option value="DQ">HLA-DQB1 ({analysis.dqb1})</option>
            <option value="DP">HLA-DPB1 ({analysis.dpb1})</option>
          </select>

          <span className="font-semibold text-gray-700 ml-2">Evidence:</span>
          <select
            value={evidenceFilter}
            onChange={(e) => setEvidenceFilter(e.target.value)}
            className="p-1.5 bg-white border border-gray-300 rounded font-medium text-gray-700"
          >
            <option value="ALL">All Tiers</option>
            <option value="Confirmed">Confirmed (Ab-Verified)</option>
            <option value="Theoretical">Theoretical</option>
          </select>
        </div>

        <div className="relative w-full sm:w-64">
          <input
            type="text"
            placeholder="Search eplet (e.g. 62GE, 52PQ)..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 bg-white border border-gray-300 rounded text-xs focus:ring-1 focus:ring-indigo-500 outline-none"
          />
          <FaSearch className="absolute left-2.5 top-2.5 text-gray-400" />
        </div>
      </div>

      {/* 5. Mismatched Eplet Table */}
      <div className="overflow-x-auto bg-white rounded-lg border border-gray-200 shadow-sm">
        <table className="w-full text-left text-xs">
          <thead className="bg-gray-100 text-gray-700 font-semibold uppercase tracking-wider text-[11px]">
            <tr>
              <th className="py-2.5 px-3">Eplet ID</th>
              <th className="py-2.5 px-3">Locus</th>
              <th className="py-2.5 px-3">Residues (Positions)</th>
              <th className="py-2.5 px-3">Evidence Tier</th>
              <th className="py-2.5 px-3">Donor Allele</th>
              <th className="py-2.5 px-3">3D Solvent Access</th>
              <th className="py-2.5 px-3">Preformed Antibody</th>
              <th className="py-2.5 px-3 text-center">3D pHLA3D</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 text-gray-700 font-normal">
            {filteredEplets.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-6 text-center text-gray-400 italic">
                  No mismatched eplets found matching the selected filter criteria.
                </td>
              </tr>
            ) : (
              filteredEplets.map((item, idx) => (
                <tr
                  key={item.id + idx}
                  className={`hover:bg-indigo-50/40 transition-colors ${
                    item.isDsa ? "bg-red-50/60" : ""
                  }`}
                >
                  <td className="py-2 px-3 font-mono font-bold text-indigo-700">
                    {item.id}
                  </td>
                  <td className="py-2 px-3">
                    <span className="px-2 py-0.5 bg-gray-100 rounded text-[10px] font-semibold text-gray-600">
                      {item.locus}
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    <span className="font-mono font-medium text-gray-900">
                      {item.residues}
                    </span>{" "}
                    <span className="text-[10px] text-gray-500">
                      ({item.positions ? item.positions.join(", ") : "N/A"})
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    <span
                      className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                        item.evidence === "Confirmed"
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {item.evidence === "Confirmed" ? "Ab-Verified" : "Theoretical"}
                    </span>
                  </td>
                  <td className="py-2 px-3 font-mono text-[11px] text-gray-800">
                    {item.sourceAllele || "-"}
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-1.5">
                      <div className="w-12 bg-gray-200 rounded-full h-1.5 overflow-hidden">
                        <div
                          className="bg-indigo-600 h-1.5 rounded-full"
                          style={{ width: `${Math.round((item.exposition || 0.8) * 100)}%` }}
                        />
                      </div>
                      <span className="text-[10px] text-gray-500 font-mono">
                        {item.exposition ? item.exposition.toFixed(2) : "0.80"}
                      </span>
                    </div>
                  </td>
                  <td className="py-2 px-3">
                    {item.isDsa ? (
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold ${
                          item.matchedMfi > 10000
                            ? "bg-red-600 text-white animate-pulse"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        <FaVial className="text-[9px]" />
                        DSA (MFI: {Math.round(item.matchedMfi).toLocaleString()})
                      </span>
                    ) : (
                      <span className="text-[10px] text-gray-400">None detected</span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-center">
                    <a
                      href={item.pHLA3DUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Inspect 3D molecular coordinates on pHLA3D"
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold text-indigo-600 bg-indigo-50 rounded hover:bg-indigo-100 transition-colors"
                    >
                      View 3D <FaExternalLinkAlt className="text-[8px]" />
                    </a>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 6. Footer Scientific Citation */}
      <div className="p-3 bg-gray-50 rounded-lg text-[11px] text-gray-500 flex flex-col sm:flex-row items-center justify-between gap-2 border border-gray-200">
        <div>
          <span>Reference: </span>
          <span className="italic font-medium">
            Duquesnoy RJ et al. HLA Epitope Registry (IHIW). Wiebe et al. J Am Soc Nephrol 2017.
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-green-500"></span>
          <span className="font-semibold text-gray-700">EpRegistry v2024.1 Indexed</span>
        </div>
      </div>
    </div>
  );
};

export default EpletAnalysisComponent;
